import { useEffect, useRef, useState } from 'react';
import { listComments, postComment, updateComment, deleteComment, toggleReaction } from '../../services/api.js';
import { getCurrentUserId, getAccessToken } from '../../services/auth.js';
import styles from './CommentsPanel.module.css';

const EMOJI_CATEGORIES = [
  { label: 'Racing',  emojis: ['🏎️','🏁','🔥','⚡','💨','🏆','🥇','🎯','🛞','⛽','🔧','⏱️','💥','🚦'] },
  { label: 'Gesture', emojis: ['👍','👎','👏','🙌','💪','🤙','✌️','🫡','👌','🤌','🤞','🤷','🫶','✊'] },
  { label: 'Mood',    emojis: ['😊','😄','😎','🤔','😮','😅','😬','🤯','😤','😡','🤩','😐','🙄','😬'] },
  { label: 'Status',  emojis: ['✅','❌','⚠️','🔴','🟡','🟢','📍','📝','📊','📈','💬','🔔','🔒','🆕'] },
];

function fmtTime(isoStr) {
  const d = new Date(isoStr);
  const diffMs = Date.now() - d.getTime();
  const diffMin = Math.floor(diffMs / 60_000);
  const diffHr  = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHr  / 24);
  if (diffMin < 1)  return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHr  < 24) return `${diffHr}h ago`;
  if (diffDay < 7)  return `${diffDay}d ago`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export default function CommentsPanel({ sessionId, canComment, corners = [], turnsEnabled = true, publicMode = false }) {
  const currentUserId = getCurrentUserId();

  const [comments,          setComments]          = useState([]);
  const [loading,           setLoading]           = useState(true);
  const [draft,             setDraft]             = useState('');
  const [posting,           setPosting]           = useState(false);
  const [error,             setError]             = useState('');
  const [deletingId,        setDeletingId]        = useState(null);
  const [editingId,         setEditingId]         = useState(null);
  const [editDraft,         setEditDraft]         = useState('');
  const [savingEdit,        setSavingEdit]        = useState(false);
  const [filterText,        setFilterText]        = useState('');
  const [filterUser,        setFilterUser]        = useState('');
  const [annotating,        setAnnotating]        = useState(false);
  const [capturedTrace,     setCapturedTrace]     = useState(null);
  const [selectedCornerIds, setSelectedCornerIds] = useState([]);
  const [showEmojiPicker,      setShowEmojiPicker]      = useState(false);
  const [reactionPickerForId,  setReactionPickerForId]  = useState(null);

  const listRef        = useRef(null);
  const textareaRef    = useRef(null);
  const pickerRef      = useRef(null);
  const emojiToggleRef = useRef(null);

  const authors = Array.from(
    new Map(comments.map(c => [c.author_id, c.author_display_name || c.author_email])).entries()
  ).map(([id, label]) => ({ id, label }));

  const filteredComments = comments.filter(c => {
    const matchText = !filterText || c.body.toLowerCase().includes(filterText.toLowerCase());
    const matchUser = !filterUser || c.author_id === filterUser;
    return matchText && matchUser;
  });

  useEffect(() => {
    if (!turnsEnabled) setSelectedCornerIds([]);
  }, [turnsEnabled]);

  useEffect(() => {
    if (!sessionId) return;

    setLoading(true);
    listComments(sessionId)
      .then(setComments)
      .catch(() => setComments([]))
      .finally(() => setLoading(false));

    // Public (account-free) viewers have no access token, so the authenticated SSE
    // stream isn't available — comments load once, read-only, without live updates.
    if (publicMode) return;

    const token = getAccessToken();
    const es = new EventSource(`/api/sessions/${sessionId}/comments/stream?token=${encodeURIComponent(token)}`);

    es.addEventListener('comment_added', (e) => {
      const data = JSON.parse(e.data);
      // Deduplicate: the poster already has this comment from the POST response
      setComments(prev => prev.some(c => c.id === data.id) ? prev : [...prev, data]);
    });

    es.addEventListener('comment_updated', (e) => {
      const data = JSON.parse(e.data);
      setComments(prev => prev.map(c => c.id === data.id ? { ...c, body: data.body } : c));
    });

    es.addEventListener('comment_deleted', (e) => {
      const data = JSON.parse(e.data);
      setComments(prev => prev.filter(c => c.id !== data.id));
    });

    return () => es.close();
  }, [sessionId]);

  useEffect(() => {
    const list = listRef.current;
    if (list) list.scrollTop = list.scrollHeight;
  }, [comments]);

  useEffect(() => {
    const handler = (e) => {
      setCapturedTrace(e.detail);
      setAnnotating(false);
    };
    window.addEventListener('trackapp:annotation-captured', handler);
    return () => window.removeEventListener('trackapp:annotation-captured', handler);
  }, []);

  useEffect(() => {
    if (!showEmojiPicker) return;
    function onOutsideClick(e) {
      if (
        pickerRef.current && !pickerRef.current.contains(e.target) &&
        emojiToggleRef.current && !emojiToggleRef.current.contains(e.target)
      ) setShowEmojiPicker(false);
    }
    document.addEventListener('mousedown', onOutsideClick);
    return () => document.removeEventListener('mousedown', onOutsideClick);
  }, [showEmojiPicker]);

  function insertEmoji(emoji) {
    const ta = textareaRef.current;
    const start = ta ? ta.selectionStart : draft.length;
    const end   = ta ? ta.selectionEnd   : draft.length;
    const newVal = draft.slice(0, start) + emoji + draft.slice(end);
    setDraft(newVal);
    setShowEmojiPicker(false);
    if (ta) {
      requestAnimationFrame(() => {
        ta.focus();
        const pos = start + emoji.length;
        ta.setSelectionRange(pos, pos);
      });
    }
  }

  async function handleReaction(commentId, emoji) {
    setReactionPickerForId(null);
    try {
      const updated = await toggleReaction(sessionId, commentId, emoji);
      setComments(prev => prev.map(c => c.id === commentId ? { ...c, reactions: updated } : c));
    } catch (err) {
      console.error('Reaction failed:', err.message);
    }
  }

  function toggleAnnotating() {
    const next = !annotating;
    setAnnotating(next);
    if (!next) setCapturedTrace(null);
    window.dispatchEvent(new CustomEvent('trackapp:annotate-mode', { detail: { active: next } }));
  }

  async function handlePost(e) {
    e.preventDefault();
    const text = draft.trim();
    if (!text) return;
    setPosting(true);
    setError('');
    try {
      const annotation = {};
      if (capturedTrace) {
        annotation.trace_channel       = capturedTrace.channel;
        annotation.trace_channel_label = capturedTrace.channelLabel;
        annotation.trace_dist_ft_start = capturedTrace.distFtStart;
        annotation.trace_dist_ft_end   = capturedTrace.distFtEnd;
      }
      if (selectedCornerIds.length > 0) annotation.corner_refs = selectedCornerIds;

      const comment = await postComment(sessionId, text, annotation);
      setComments(prev => prev.some(c => c.id === comment.id) ? prev : [...prev, comment]);
      setDraft('');
      setCapturedTrace(null);
      setSelectedCornerIds([]);
      textareaRef.current?.focus();
    } catch (err) {
      setError(err.message || 'Failed to post comment');
    } finally {
      setPosting(false);
    }
  }

  function startEdit(c) {
    setEditingId(c.id);
    setEditDraft(c.body);
  }

  function cancelEdit() {
    setEditingId(null);
    setEditDraft('');
  }

  async function handleSaveEdit(commentId) {
    const text = editDraft.trim();
    if (!text) return;
    setSavingEdit(true);
    try {
      const updated = await updateComment(sessionId, commentId, text);
      setComments(prev => prev.map(c => c.id === commentId ? updated : c));
      cancelEdit();
    } catch (err) {
      alert(`Failed to save: ${err.message}`);
    } finally {
      setSavingEdit(false);
    }
  }

  async function handleDelete(commentId) {
    setDeletingId(commentId);
    try {
      await deleteComment(sessionId, commentId);
      setComments(prev => prev.filter(c => c.id !== commentId));
    } catch (err) {
      alert(`Failed to delete: ${err.message}`);
    } finally {
      setDeletingId(null);
    }
  }

  return (
    <div className={styles.panel}>
      {!loading && comments.length > 0 && (
        <div className={styles.filterBar}>
          <input
            className={styles.filterInput}
            type="text"
            placeholder="Search comments…"
            value={filterText}
            onChange={e => setFilterText(e.target.value)}
          />
          <select
            className={styles.filterSelect}
            value={filterUser}
            onChange={e => setFilterUser(e.target.value)}
          >
            <option value="">All users</option>
            {authors.map(a => (
              <option key={a.id} value={a.id}>{a.label}</option>
            ))}
          </select>
        </div>
      )}
      <div className={styles.list} ref={listRef}>
        {loading && <p className={styles.empty}>Loading…</p>}
        {!loading && comments.length === 0 && (
          <p className={styles.empty}>No comments yet.{canComment ? ' Be the first!' : ''}</p>
        )}
        {!loading && comments.length > 0 && filteredComments.length === 0 && (
          <p className={styles.empty}>No comments match your filter.</p>
        )}
        {filteredComments.map(c => (
          <div key={c.id} className={styles.comment}>
            <div className={styles.commentHeader}>
              <span className={styles.author}>
                {c.author_display_name || c.author_email}
              </span>
              <span className={styles.time} title={new Date(c.created_at).toLocaleString()}>
                {fmtTime(c.created_at)}
              </span>
              {c.author_id === currentUserId && (
                <div className={styles.commentActions}>
                  {editingId !== c.id && (
                    <button
                      className={styles.editBtn}
                      onClick={() => startEdit(c)}
                      disabled={deletingId === c.id}
                      title="Edit comment"
                    >✏</button>
                  )}
                  <button
                    className={styles.deleteBtn}
                    onClick={() => handleDelete(c.id)}
                    disabled={deletingId === c.id || editingId === c.id}
                    title="Delete comment"
                  >
                    {deletingId === c.id ? '…' : '×'}
                  </button>
                </div>
              )}
            </div>
            {editingId === c.id ? (
              <div className={styles.editArea}>
                <textarea
                  className={styles.editTextarea}
                  value={editDraft}
                  onChange={e => setEditDraft(e.target.value)}
                  rows={3}
                  autoFocus
                  onKeyDown={e => {
                    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) handleSaveEdit(c.id);
                    if (e.key === 'Escape') cancelEdit();
                  }}
                />
                <div className={styles.editActions}>
                  <button
                    className={styles.saveBtn}
                    onClick={() => handleSaveEdit(c.id)}
                    disabled={savingEdit || !editDraft.trim()}
                  >
                    {savingEdit ? '…' : 'Save'}
                  </button>
                  <button
                    className={styles.cancelBtn}
                    onClick={cancelEdit}
                    disabled={savingEdit}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <>
                <p className={styles.body}>{c.body}</p>
                {(c.trace_channel || c.corner_refs?.length > 0) && (
                  <div className={styles.badges}>
                    {c.trace_channel && (
                      <button
                        type="button"
                        className={styles.traceBadge}
                        title={`Zoom to ${c.trace_channel_label} · ${Math.round(c.trace_dist_ft_start).toLocaleString()}–${Math.round(c.trace_dist_ft_end).toLocaleString()} ft`}
                        onClick={() => window.dispatchEvent(new CustomEvent('trackapp:chart-zoom', {
                          detail: { distFtMin: c.trace_dist_ft_start, distFtMax: c.trace_dist_ft_end },
                        }))}
                      >
                        {c.trace_channel_label} · {Math.round(c.trace_dist_ft_start).toLocaleString()}–{Math.round(c.trace_dist_ft_end).toLocaleString()} ft
                      </button>
                    )}
                    {c.corner_refs?.map(id => {
                      const corner = corners.find(cr => cr.id === id);
                      return (
                        <span key={id} className={styles.cornerBadge}>
                          {corner?.label ?? id}
                        </span>
                      );
                    })}
                  </div>
                )}
                <div className={styles.reactionRow}>
                  {(c.reactions ?? []).map(r => (
                    <button
                      key={r.emoji}
                      type="button"
                      className={`${styles.reactionPill} ${r.reacted_by_me ? styles.reactionPillActive : ''}`}
                      onClick={() => canComment && handleReaction(c.id, r.emoji)}
                      title={r.reacted_by_me ? 'Remove reaction' : 'Add reaction'}
                      disabled={!canComment}
                    >
                      {r.emoji} <span className={styles.reactionCount}>{r.count}</span>
                    </button>
                  ))}
                  {canComment && (
                    <button
                      type="button"
                      className={`${styles.addReactionBtn} ${reactionPickerForId === c.id ? styles.addReactionBtnActive : ''}`}
                      onClick={() => setReactionPickerForId(id => id === c.id ? null : c.id)}
                      title="Add reaction"
                    >
                      {reactionPickerForId === c.id ? '×' : '+'}
                    </button>
                  )}
                </div>
                {reactionPickerForId === c.id && (
                  <div className={styles.reactionPicker}>
                    {['👍','👎','❤️','😄','❗','❓','😊'].map(em => (
                      <button
                        key={em}
                        type="button"
                        className={styles.reactionOpt}
                        onClick={() => handleReaction(c.id, em)}
                      >
                        {em}
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        ))}
      </div>

      {canComment ? (
        <form className={styles.composer} onSubmit={handlePost}>
          {error && <p className={styles.error}>{error}</p>}

          <div className={styles.annotateBar}>
            <button
              type="button"
              className={`${styles.annotateBtn} ${annotating ? styles.annotateBtnActive : ''}`}
              onClick={toggleAnnotating}
            >
              {annotating ? 'Cancel' : '📍 Attach trace section'}
            </button>
            {annotating && (
              <span className={styles.annotateHint}>Drag on any chart to capture a range</span>
            )}
            {capturedTrace && (
              <span className={styles.traceChip}>
                {capturedTrace.channelLabel} · {Math.round(capturedTrace.distFtStart).toLocaleString()}–{Math.round(capturedTrace.distFtEnd).toLocaleString()} ft
                <button
                  type="button"
                  className={styles.chipRemove}
                  onClick={() => setCapturedTrace(null)}
                >×</button>
              </span>
            )}
            {turnsEnabled && corners.length > 0 && (
              <select
                multiple
                className={styles.cornerSelect}
                value={selectedCornerIds}
                onChange={e => setSelectedCornerIds(Array.from(e.target.selectedOptions, o => o.value))}
                title="Hold Ctrl/Cmd to select multiple corners"
              >
                {corners.map(c => (
                  <option key={c.id} value={c.id}>{c.label || c.id}</option>
                ))}
              </select>
            )}
            {turnsEnabled && selectedCornerIds.length > 0 && (
              <div className={styles.cornerChips}>
                {selectedCornerIds.map(id => {
                  const corner = corners.find(c => c.id === id);
                  return (
                    <span key={id} className={styles.cornerChip}>
                      {corner?.label ?? id}
                      <button
                        type="button"
                        className={styles.chipRemove}
                        onClick={() => setSelectedCornerIds(prev => prev.filter(x => x !== id))}
                      >×</button>
                    </span>
                  );
                })}
              </div>
            )}
          </div>

          <textarea
            ref={textareaRef}
            className={styles.textarea}
            value={draft}
            onChange={e => { setDraft(e.target.value); setError(''); }}
            placeholder="Add a comment… (Ctrl+Enter to post)"
            rows={2}
            onKeyDown={e => {
              if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                e.preventDefault();
                handlePost(e);
              }
            }}
          />
          <div className={styles.composerFooter}>
            <div className={styles.emojiWrapper}>
              <button
                ref={emojiToggleRef}
                type="button"
                className={`${styles.emojiToggleBtn} ${showEmojiPicker ? styles.emojiToggleBtnActive : ''}`}
                onClick={() => setShowEmojiPicker(p => !p)}
                title="Insert emoji"
              >
                😊
              </button>
              {showEmojiPicker && (
                <div className={styles.emojiPicker} ref={pickerRef}>
                  {EMOJI_CATEGORIES.map(cat => (
                    <div key={cat.label} className={styles.emojiCat}>
                      <div className={styles.emojiCatLabel}>{cat.label}</div>
                      <div className={styles.emojiGrid}>
                        {cat.emojis.map(em => (
                          <button
                            key={em}
                            type="button"
                            className={styles.emojiOpt}
                            onClick={() => insertEmoji(em)}
                          >
                            {em}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <button
              className={styles.postBtn}
              type="submit"
              disabled={posting || !draft.trim()}
            >
              {posting ? '…' : 'Post'}
            </button>
          </div>
        </form>
      ) : (
        <p className={styles.viewOnly}>View-only access — commenting disabled</p>
      )}
    </div>
  );
}
